import ko from "knockout";
import userContext from "flow/viewModels/userContext";
import viewportFactory from "flow/untested/viewportFactory";
import router from "flow/router";
import manifestManager from "flow/utils/manifestManager";
import menu from "flow/viewModels/menu";
import workingContext from "flow/viewModels/workingContext";
import config from "flow/config";
import activator from "flow/utils/activator";
import baseUrl from "flow/wrappers/baseUrl";
import dialog from "flow/utils/dialog";
import myFlowFactory from "flow/factories/myFlowFactory";
import signalrEventBridge from "flow/events/signalrEventBridge";
import metaCache from "flow/utils/metaCache";
import dataservice from "flow/data/service";
import userContextTabs from "flow/viewModels/userContextTabs";
import sideTabs from "flow/viewModels/sideTabs";
import { systemInformationViewModel } from "flow/systeminfo/systemInformation";
import MyFlow from "flow/MyFlow";
import { IBreadcrumb } from "flow/breadcrumbTracker";
import ToolbarItemManifestEx from "flow/manifests/ToolbarItemManifestEx";
import Viewport, { CanLeaveEntry } from "flow/Viewport";
import bind from "bind-decorator";
import { ImpersonatedUser } from "flow/entities/ImpersonatedUser";
import { ManifestConstants } from "flow/entities/ManifestConstants";
import environment from "flow/configuration/environment";

export class Workspace {
    userContext = userContext;
    userContextTabs = userContextTabs;
    workingContext = workingContext;
    menu = menu;
    sideTabs = sideTabs;
    breadcrumbs = router.breadcrumbs;
    currentBreadcrumbTitle = router.currentBreadcrumbTitle;
    systemInformationViewModel = systemInformationViewModel;
    debugOrder = ko.observable<boolean>(false);

    private navigationPanelItemsAll = ko.observableArray<ToolbarItemManifestEx>();
    navigationPanelItems = ko.pureComputed<ToolbarItemManifestEx[]>(() =>
        this.navigationPanelItemsAll().filter(item => item.shouldBeVisible(workingContext.currentId()))
    );
    hasCustomMenuFeature = ko.observable<boolean>(false);
    isWorkspaceLoading = ko.observable<boolean>(false);
    mobileMenuExpanded = ko.observable<boolean>(false);
    simpleWorkspace = ko.observable<boolean>(false);
    viewport = ko.observable<Viewport>();
    isLoading = ko.pureComputed<boolean>(() => {
        return this.isWorkspaceLoading() || workingContext.isLoading();
    });
    showQuickLogOut = ko.observable<boolean>(false);
    isMenuExpanded = ko.observable<boolean>(false);
    ariaLabelToggleMenu = ko.pureComputed<string>(() => {
        return this.isMenuExpanded()
            ? metaCache.translate("FlowHtml.Translations.CollapseMenu")
            : metaCache.translate("FlowHtml.Translations.ExpandMenu");
    });
    selectedNavigationPanelItemId = ko.observable<string>("");
    impersonatedUser = ko.observable<ImpersonatedUser>();

    homeConfigurationEnabled = ko.observable<boolean>(false);
    showHomeConfiguration = ko.pureComputed<boolean>(
        () => this.homeConfigurationEnabled() && this.workingContext.isHomeScreen()
    );

    environmentBannerText = ko.observable<string>("");

    private myFlow: MyFlow;

    productName = ko.observable<string>("");

    constructor() {
        menu.isVisible.subscribe(() => {
            this.myFlow.publish(config.topics.menuChanged, this.isMenuExpanded() || menu.isVisible());
        });
    }

    init(): void {
        this.myFlow.envSubscribe(environment.keys.debugOrder, this.debugOrder, true);
        this.myFlow.envSubscribe(environment.keys.homeConfiguration, this.homeConfigurationEnabled, true);
        this.myFlow.envSubscribe(environment.keys.environmentBanner, this.environmentBannerText, true);

        this.myFlow.subscribe(config.topics.navigationPanelItemSelected, id => {
            if (this.inNavigationPanelList(id)) {
                this.selectedNavigationPanelItemId(id);
            } else if (menu.currentId() === this.selectedNavigationPanelItemId() && menu.isVisible()) {
                // Corner case, do nothing
            } else {
                this.selectedNavigationPanelItemId(id);
            }
        });

        this.myFlow.subscribe(config.topics.mobileMenuHide, () => {
            this.mobileMenuExpanded(false);
        });

        this.myFlow.subscribe(config.topics.mobileMenuShow, () => {
            this.mobileMenuExpanded(true);
        });

        this.showQuickLogOut(metaCache.lookup<boolean>("Common.Features.FlowHtml_ShowQuickLogOut", false) == true);

        this.navigationPanelItemsAll(this.getNavigationPanelItems());

        this.hasCustomMenuFeature(metaCache.lookup<boolean>("Common.Features.HasCustomMenu", false) == true);
        if (this.hasCustomMenuFeature() === false) {
            this.navigationPanelItemsAll.unshift({
                id: config.componentIds.homeContext,
                icon: config.images.home,
                tooltip: this.myFlow.translate(config.metaKeys.home),
                name: this.myFlow.translate(config.metaKeys.home),
                namespace: undefined,
                shouldBeVisible: function () {
                    return true;
                },
                position: "",
                viewModelId: undefined,
                order: ManifestConstants.ToolbarItemOrderHomeValue,
                openInNewTab: false,
                contextIds: undefined
            });
        }
        const menuManifests = manifestManager.getAutoStartMenuManifests();
        if (menuManifests.length > 0) {
            this.myFlow.activateById(menuManifests[0].id);
        }

        signalrEventBridge.init(this.myFlow);
        userContextTabs.init(manifestManager.getUserContextTabManifests());
        sideTabs.init(this.myFlow, manifestManager.getSideTabManifests());

        this.productName(metaCache.lookup("FlowHtml.ProductName", ""));
        this.impersonatedUser(manifestManager.getImpersonatedUser());
    }

    earlySetup(): Viewport {
        this.viewport(viewportFactory.createViewport(this.myFlow));
        menu.init(this.myFlow);
        activator.init(this.viewport(), menu, router, dialog, workingContext, userContextTabs, this.myFlow, sideTabs);
        return this.viewport();
    }

    inNavigationPanelList(id: string): boolean {
        for (let i = 0; i < this.navigationPanelItemsAll().length; i++) {
            if (this.navigationPanelItemsAll()[i].id === id) {
                return true;
            }
        }
        return false;
    }

    getNavigationPanelItems(): ToolbarItemManifestEx[] {
        const menuItems = manifestManager.getNavigationPanelItems();
        menuItems.forEach(menuItem => {
            menuItem.viewModel = ko.observable();
            if (menuItem.viewModelId) {
                const manifest = manifestManager.getManifestWithId(menuItem.id);
                const toolbarItemMyFlow = myFlowFactory.createMyFlow(manifest, {
                    authorizationManager: manifestManager.getWebModuleAuthorizationManager(manifest.moduleId)
                });
                dataservice.getViewModel(toolbarItemMyFlow, menuItem.viewModelId).then((ToolbarItemViewModel: any) => {
                    if (manifest.toolbarItem.namespace) {
                        ToolbarItemViewModel = ToolbarItemViewModel[manifest.toolbarItem.namespace];
                    }
                    const toolbarItemViewModel = new ToolbarItemViewModel(toolbarItemMyFlow);
                    toolbarItemViewModel.activate();
                    menuItem.viewModel(toolbarItemViewModel);
                });
            }
        });
        return menuItems;
    }

    @bind
    reportUncaughtError(error: Event, filePath: string, lineNo: number, columnNo: number): void {
        if (filePath === "" && lineNo === 0 && columnNo === 0) {
            return;
        }

        if (filePath.match(new RegExp(config.partialUrls.heartbeat)) === null) {
            this.myFlow.error("Uncaught error: " + error + ", in " + filePath + " on line " + lineNo);
            this.myFlow.notifyError("FlowHtml: " + this.myFlow.translate("FlowHtml.Translations.UnknownError"));
        }
    }

    @bind
    reportUnhandledRejection(event: PromiseRejectionEvent): void {
        if (event.reason && event.reason.handled) {
            return;
        }

        this.myFlow.error("Uncaught rejection", event.reason);
        this.myFlow.notifyError("FlowHtml: " + this.myFlow.translate("FlowHtml.Translations.UnknownError"));
        if (event.preventDefault) {
            event.preventDefault();
        }
    }

    getMyFlow(): MyFlow {
        return this.myFlow;
    }

    createMyFlow(): MyFlow {
        this.myFlow = myFlowFactory.createMyFlow(
            { id: config.flowId, baseUrl: baseUrl, moduleId: config.moduleId, moduleKeyName: config.moduleKeyName },
            {
                exception: () => {
                    this.myFlow.notifyError("FlowHtml: " + this.myFlow.translate("FlowHtml.Translations.UnknownError"));
                },
                startLoading: () => {
                    this.isWorkspaceLoading(true);
                },
                stopLoading: () => {
                    this.isWorkspaceLoading(false);
                }
            }
        );

        this.myFlow.disableGenericAjaxErrorHandler();

        return this.myFlow;
    }

    onToolbarItem(item: ToolbarItemManifestEx): void {
        if (item.openInNewTab === true) {
            this.myFlow.activateByIdInNewTab(item.id);
            return;
        }

        if (
            (!manifestManager.isMenu(menu.currentId()) && menu.currentId() === item.id) ||
            !activator.canLeaveSweepWithUserFeedback(item.id)
        ) {
            return;
        }

        if (manifestManager.isWorkingContext(item.id) || manifestManager.isFunction(item.id)) {
            router.cleanHistory(item.id);
            menu.isVisible(false);
            this.mobileMenuExpanded(false);
        }

        this.myFlow.activateById(item.id);
    }

    collectCanLeaveResults(id?: string): CanLeaveEntry[] {
        return activator.collectCanLeaveResults(id);
    }

    toggleMobileMenu(): void {
        this.mobileMenuExpanded(!this.mobileMenuExpanded());
    }

    toggleMenu(): void {
        if (this.isMenuExpanded()) {
            this.isMenuExpanded(false);
        } else if (!this.isMenuExpanded() && !menu.isVisible()) {
            this.isMenuExpanded(true);
        }

        if (menu.isVisible() && workingContext.currentId() === config.componentIds.homeContext) {
            this.myFlow.publish(config.topics.navigationPanelItemSelected, config.componentIds.homeContext);
        }

        menu.onHide();

        this.myFlow.publish(config.topics.menuChanged, this.isMenuExpanded() || menu.isVisible());
    }

    activateFunctionComponent(id: string): JQueryPromise<void> {
        const dfd = this.myFlow.deferred<void>();

        try {
            this.myFlow.activateById(id);
            dfd.resolve();
        } catch (exception) {
            dfd.reject();
        }

        return dfd.promise();
    }

    onBreadcrumbItem(breadcrumbItem: IBreadcrumb): void {
        if (!activator.canLeaveSweepWithUserFeedback(breadcrumbItem.id)) {
            return;
        }

        router.navigateBasedOnFragment(breadcrumbItem.id);
    }

    navigateBack(): void {
        if (!activator.canLeaveSweepWithUserFeedback()) {
            return;
        }

        router.navigateBack();
    }

    /*
            When Health Care Desktop launches SC Desktop, HC Desktop sets global variable LifecareObject.
    
            If social care desktop is lauched from health care desktop as embedded view
            then bread crumbs, top banner and menu must be hidden
            because HC desktop already show same infromation.
            */
    isLaunchedFromHCDesktop(): boolean {
        let launchedFromHCDesktop = false;
        if (typeof (window as any).LifecareObject != "undefined") {
            launchedFromHCDesktop = true;
        }
        return launchedFromHCDesktop;
    }

    showTopBannerAndBreadcrumbs(): boolean {
        return !this.isLaunchedFromHCDesktop();
    }

    showMenu(): boolean {
        return !this.isLaunchedFromHCDesktop();
    }

    activateCreditComponent(): void {
        this.myFlow.activateById(config.componentIds.credit);
        userContextTabs.toggle();
    }

    getAllNavigationPanelItems() {
        return this.navigationPanelItemsAll();
    }
}

const workspace = new Workspace();
export default workspace;
